import { call } from 'redux-saga/effects';
import { RTCClient, ToggleMediaTemplate } from 'utils/webrtc';
import { batchToggleMediaTemplate, sendMessage } from 'utils/webrtc/messages';
import { UserId } from 'features/users/types';
import { MediaStreamInfo } from 'features/streaming/types';

export function* subscribeToScreenshareSaga(
  userId: UserId,
  feedId: string,
  streams: MediaStreamInfo[],
  options: {
    videoOnly?: boolean;
    forceSubscribe?: boolean;
  } = {}
) {
  const { videoOnly = true, forceSubscribe = false } = options;

  if (!streams?.length) {
    return;
  }

  const filteredStreams = videoOnly ? streams.filter((stream) => stream.type === 'video') : streams;

  if (!filteredStreams.length && !forceSubscribe) {
    return;
  }

  if (RTCClient.receivingFeed.subscribedScreenshares[feedId]) {
    const feed = RTCClient.getJanusPlugin();

    if (!feed) {
      return;
    }

    const { streams: existingStreams } = RTCClient.receivingFeed.media.getFeedStreams(feedId);

    const request = Object.values(existingStreams)
      .map((stream) => {
        let shouldSend = true;

        if (videoOnly) {
          shouldSend = stream.kind === 'video';
        }

        if (stream.subscriberMid) {
          return {
            mid: stream.subscriberMid,
            send: shouldSend,
          };
        }

        return null;
      })
      .filter(Boolean) as ToggleMediaTemplate;

    // Before `attached` no stream has a subscriberMid, and an empty `configure` on a handle that
    // has not joined draws a 424 the error saga treats as a rejected `join`.
    if (request.length) {
      yield call(sendMessage, feed, batchToggleMediaTemplate(request));
    }
  } else if (feedId && userId) {
    // mark as subscribed if the video stream has been passed
    RTCClient.receivingFeed.subscribedScreenshares[feedId] = streams.some(
      (stream) => stream.type === 'video'
    );

    const [substream, temporal] = RTCClient.receivingFeed.getBaseScreenshareSimulcastConfig();

    const streamsWithOptions = filteredStreams.map((stream) => ({
      ...stream,
      options: { temporal, substream },
    }));

    yield call(
      RTCClient.subscribe,
      {
        feedId,
        streams: streamsWithOptions,
        id: userId,
      },
      'screenshare'
    );

    yield call(RTCClient.enableScreensharingMedia, userId, streams);
  }
}
